import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Link2, CheckCircle2, Search, Video, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoLinkingPage = () => {
  const [loading, setLoading] = useState(true);
  const [unmatched, setUnmatched] = useState([]);
  const [linking, setLinking] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [filter, setFilter] = useState('');
  const [applyingId, setApplyingId] = useState(null);

  useEffect(() => { fetchUnmatched(); }, []);

  const fetchUnmatched = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/plans/unmatched-exercises`);
      setUnmatched(res.data.unmatched || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  const handleRelink = async () => {
    if (!window.confirm('Esto recorrerá todos tus planes y vinculará cada ejercicio con la librería por nombre. ¿Continuar?')) return;
    setLinking(true);
    try {
      const res = await axios.post(`${API}/admin/plans/relink-library`);
      setLinkResult(res.data);
      toast.success(`${res.data.exercises_relinked} ejercicios vinculados en ${res.data.plans_touched} planes`);
      fetchUnmatched();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setLinking(false); }
  };

  const applySuggestion = async (item, suggestion) => {
    const key = `${item.plan_id}-${item.week_index}-${item.session_index}-${item.location}-${item.exercise_index}`;
    setApplyingId(key);
    try {
      await axios.post(`${API}/admin/plans/apply-library-match`, {
        plan_id: item.plan_id,
        week_index: item.week_index,
        session_index: item.session_index,
        location: item.location,
        exercise_index: item.exercise_index,
        library_id: suggestion.id,
      });
      toast.success(`Vinculado: ${suggestion.name}`);
      // Quitar este item de la lista sin recargar todo
      setUnmatched(prev => prev.filter(u =>
        !(u.plan_id === item.plan_id && u.context === item.context && u.exercise_index === item.exercise_index && u.exercise_name === item.exercise_name)
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setApplyingId(null); }
  };

  const filteredUnmatched = useMemo(() => {
    if (!filter) return unmatched;
    const f = filter.toLowerCase();
    return unmatched.filter(u =>
      (u.exercise_name || '').toLowerCase().includes(f) ||
      (u.plan_name || '').toLowerCase().includes(f)
    );
  }, [unmatched, filter]);

  // Agrupar por plan
  const byPlan = useMemo(() => {
    const map = {};
    filteredUnmatched.forEach(u => {
      if (!map[u.plan_name]) map[u.plan_name] = [];
      map[u.plan_name].push(u);
    });
    return map;
  }, [filteredUnmatched]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="video-linking-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Link2 className="h-6 w-6 text-primary" /> Vinculación de Videos ↔ Planes
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Vincula los ejercicios de tus planes con la librería por ID. A partir de entonces los videos se mantienen estables aunque cambien nombres.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={fetchUnmatched} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refrescar
            </Button>
            <Button onClick={handleRelink} disabled={linking} data-testid="relink-btn">
              {linking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
              Vincular todos ahora
            </Button>
          </div>
        </div>

        {linkResult && (
          <Card className="bg-green-500/5 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <p className="text-sm">
                  <strong className="text-green-400">{linkResult.exercises_relinked}</strong> ejercicios vinculados en{' '}
                  <strong className="text-green-400">{linkResult.plans_touched}</strong> planes.
                  {linkResult.unmatched_count > 0 && (
                    <> Quedan <strong className="text-amber-400">{linkResult.unmatched_count}</strong> sin match que puedes revisar abajo.</>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtro */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Buscar por nombre de ejercicio o plan..."
            className="bg-zinc-900/50 border-zinc-800 pl-10"
            data-testid="filter-input"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : unmatched.length === 0 ? (
          <Card className="bg-card border-dashed">
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-green-400 mb-3" />
              <p className="text-lg font-semibold text-foreground">¡Todo vinculado!</p>
              <p className="text-sm text-muted-foreground mt-1">Todos los ejercicios de tus planes tienen match con la librería.</p>
            </CardContent>
          </Card>
        ) : Object.keys(byPlan).length === 0 ? (
          <p className="text-sm text-muted-foreground italic text-center py-6">Sin resultados para "{filter}"</p>
        ) : (
          <div className="space-y-5">
            {Object.entries(byPlan).map(([planName, items]) => (
              <Card key={planName} className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-400" />
                    {planName} <Badge variant="outline">{items.length} sin match</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {items.map((item, i) => {
                    const key = `${item.plan_id}-${item.week_index}-${item.session_index}-${item.location}-${item.exercise_index}`;
                    return (
                      <div key={i} className="p-3 bg-zinc-900/50 rounded">
                        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="font-medium text-foreground truncate">"{item.exercise_name || '(sin nombre)'}"</span>
                            <Badge variant="outline" className="text-xs">{item.context}</Badge>
                          </div>
                        </div>
                        {item.suggestions?.length > 0 ? (
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Sugerencias de la librería:</p>
                            {item.suggestions.map((sug, j) => (
                              <div key={j} className="flex items-center justify-between gap-2 p-2 rounded border border-zinc-800 hover:border-primary/30 transition-colors">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <Badge variant="outline" className={`text-xs ${sug.score >= 0.5 ? 'border-amber-500/40 text-amber-400' : 'border-zinc-700 text-muted-foreground'}`}>
                                    {Math.round(sug.score * 100)}%
                                  </Badge>
                                  {sug.has_video && <Video className="h-3 w-3 text-green-400 flex-shrink-0" title="Tiene video" />}
                                  <span className="text-sm truncate">{sug.name}</span>
                                </div>
                                <Button size="sm" variant="outline"
                                  onClick={() => applySuggestion(item, sug)}
                                  disabled={applyingId === key}
                                  data-testid={`apply-${key}-${j}`}>
                                  {applyingId === key ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Usar'}
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">Sin sugerencias. Renombra el ejercicio en el plan o en la librería.</p>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default VideoLinkingPage;
